import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@emotion/react';
import RouteProtector from './components/RouteProtector';
import GlobalContextProvider from './contexts/GlobalContext';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

import Layout from './layouts/Layout';
import LayoutAdmin from './layouts/LayoutAdmin';

import Register from './pages/Register/Register';
import ManageHome from './pages/ManageHome';
import StudentDetail from './pages/StudentDetail';
import AddToEvent from './pages/AddToEvent';
import Homepage from './pages/Homepage';
import Students from './pages/Students';
import NotFound from './pages/NotFound';
import Settings from './pages/Settings';
import Classes from './pages/Classes';
import Events from './pages/Events';
import Login from './pages/Login';
import Logout from './pages/Logout';
import EventDetail from './pages/EventDetail';
import Upload from './pages/Upload';
import Preamble from './pages/Preamble';
import NominalRole from './pages/NominalRole';
import HomeCats from './pages/HomeCats';
import EditStudentDetail from './pages/EditStudentDetail';
import Payments from './pages/Payments';
import EditParentDetails from './pages/EditParentDetails';
import AddParent from './pages/AddParent';
import Parents from './pages/Parents';
import UpdateDetails from './pages/UpdateDetails';
import UpdateSacrament from './pages/UpdateSacrament';

const colors = require('tailwindcss/colors');
const App = () => {
  const router = createBrowserRouter([
    {
      element: <Layout />,
      errorElement: <NotFound />,
      children: [
        {
          path: '/',
          element: <Homepage />,
        },
        {
          path: 'preamble',
          element: <Preamble />,
        },
        {
          path: 'register',
          element: <Register />,
        },
        {
          path: 'register/:page',
          element: <Register />,
        },
        {
          path: 'register/upload',
          element: <Upload />,
        },
        {
          path: 'update-details/:hash',
          element: <UpdateDetails />,
        },
      ],
    },
    {
      element: <LayoutAdmin />,
      errorElement: <NotFound />,
      children: [
        {
          path: '/manage',
          element: (
            <RouteProtector>
              <ManageHome />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/students',
          element: (
            <RouteProtector>
              <Students />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/students/:studentId',
          element: (
            <RouteProtector>
              <StudentDetail />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/students/edit/:studentId',
          element: (
            <RouteProtector>
              <EditStudentDetail />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/classes',
          element: (
            <RouteProtector>
              <Classes />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/classes/:levelSelection',
          element: (
            <RouteProtector>
              <Classes />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/classes/:levelSelection/nominalroll',
          element: (
            <RouteProtector>
              <NominalRole />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/events',
          element: (
            <RouteProtector>
              <Events />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/events/:eventId',
          element: (
            <RouteProtector>
              <EventDetail />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/events/:eventId/:addToEvent',
          element: (
            <RouteProtector>
              <AddToEvent />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/events/create',
          element: (
            <RouteProtector>
              <Events mode='create' />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/settings',
          element: (
            <RouteProtector>
              <Settings />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/homecats',
          element: (
            <RouteProtector>
              <HomeCats />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/payments',
          element: (
            <RouteProtector>
              <Payments />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/parents',
          element: (
            <RouteProtector>
              <Parents />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/parents/add/:student_id',
          element: (
            <RouteProtector>
              <AddParent />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/parents/edit/:parent_id',
          element: (
            <RouteProtector>
              <EditParentDetails />
            </RouteProtector>
          ),
        },
        {
          path: '/manage/update-sacrament',
          element: (
            <RouteProtector>
              <UpdateSacrament />
            </RouteProtector>
          ),
        },
      ],
    },
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/logout',
      element: <Logout />,
    },
  ]);

  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#b31315',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#8DB851',
        contrastText: '#ffffff',
      },
    },
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false, // default: true
        staleTime: 180000,
        // cacheTime: 300000,
      },
    },
  });
  return (
    <ThemeProvider theme={theme}>
      <GlobalContextProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <ReactQueryDevtools />
        </QueryClientProvider>
      </GlobalContextProvider>
    </ThemeProvider>
  );
};

export default App;
